import GeneralService from "./GeneralService";

export interface ConversationBuilderSaveRequest {
  conversation_flow: Record<number, ConversationNode>;
  placeholder_variables?: Record<
    string,
    {
      values: string[];
      persist?: boolean;
    }
  >;
  configs?: ConversationConfig;
  interjections?: Record<string, Interjection>;
  tool_calls?: ToolCallFunction[];
  metadata?: Record<string, any>;
  agent_id?: number | null; // Added to fix database parameter issue
  // Required metadata fields
  conversation_flow_name: string;
  company_id: string;
  application_ref_name: string;
  action_type: string;
  conversation_flow_version?: string;
  is_latest?: boolean;
}

export interface ConversationNode {
  role: string;
  content: string;
  is_start_node?: boolean;
  is_end_node?: boolean;
  category?: string;
  decision_logic?: DecisionConfig;
  next_possible_node_ids?: number[];
  variables?: Record<string, boolean>;
  tool_call?: ToolCallFunction;
}

export interface DecisionConfig {
  passed: number;
  failed: number;
  logic: any; // JSONLogic format
}

export interface ConversationConfig {
  start_node?: number[];
  end_node?: number[];
  decision_node_id?: number[];
  buildup?: boolean;
  deduplicate?: boolean;
  canonical?: boolean;
}

export interface Interjection {
  // Add fields as needed based on backend model
  content: string;
  trigger?: string;
}

export interface ToolCallFunction {
  name: string;
  description?: string;
  arguments: string; // JSON string format expected by backend
  response_variables?: Record<
    string,
    {
      values: string[];
      persist: boolean;
    }
  >;
}

export interface ConversationBuilderSaveResponse {
  success: boolean;
  conversation_builder_config_id: string;
  conversation_flow_name: string;
  conversation_flow_version: number;
  conversation_flow_count: number;
  conversation_flow_breakdown: Record<string, any>;
}

export class ConversationBuilderService {
  public static saveAndGetPaths(
    payload: ConversationBuilderSaveRequest,
    loadingHandler: (val: boolean) => void,
    apiKey: string
  ) {
    return GeneralService.post(
      "/conversation_builder/save_and_get_paths",
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getConversationTreeConfig(configId: number, apiKey: string) {
    return GeneralService.get(
      `/conversation_builder/${configId}`,
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static listConversationTreesByCompany(
    companyId: number,
    apiKey: string
  ) {
    return GeneralService.get(
      `/conversation_builder/list_conversation_trees/${companyId}`,
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static listLatestConversationTreesByCompany(
    companyId: number,
    apiKey: string
  ) {
    return GeneralService.get(
      `/conversation_builder/list_conversation_trees_latest/${companyId}`,
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static listConversationTreeVersions(
    companyId: number,
    applicationName: string,
    agentName: string,
    conversationFlowName: string,
    apiKey: string
  ) {
    return GeneralService.get(
      `/conversation_builder/list_conversation_tree_versions/${companyId}/${encodeURIComponent(
        applicationName
      )}/${encodeURIComponent(agentName)}/${encodeURIComponent(
        conversationFlowName
      )}`,
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static updateConversationFlowName(
    configId: number,
    newConversationFlowName: string,
    apiKey: string
  ) {
    const payload = {
      config_id: configId,
      new_conversation_flow_name: newConversationFlowName,
    };

    return GeneralService.put(
      "/conversation_builder/update_conversation_flow_name",
      JSON.stringify(payload),
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static checkConversationFlowNameConflict(
    companyId: number,
    applicationId: number,
    intentId: number,
    newConversationFlowName: string,
    apiKey: string
  ) {
    return this.validateConversationFlowName(
      companyId,
      applicationId,
      intentId,
      newConversationFlowName,
      apiKey
    );
  }

  public static getNextVersionNumber(
    companyId: number,
    applicationRefName: string,
    actionType: string,
    conversationFlowName: string,
    apiKey: string
  ) {
    return GeneralService.get(
      `/conversation_builder/next_version/${companyId}/${encodeURIComponent(
        applicationRefName
      )}/${encodeURIComponent(actionType)}/${encodeURIComponent(
        conversationFlowName
      )}`,
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static createConversationTreeFinetune(
    companyId: number,
    applicationRefName: string,
    actionType: string,
    conversationFlowName: string,
    conversationBuilderConfigId: string,
    apiKey: string
  ) {
    const payload = {
      company_id: companyId,
      application_ref_name: applicationRefName,
      action_type: actionType,
      conversation_flow_name: conversationFlowName,
      conversation_builder_config_id: conversationBuilderConfigId,
    };

    return GeneralService.post(
      "/conversation_builder/create_conversation_tree_finetune",
      JSON.stringify(payload),
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static createConversationTreeResources(
    companyId: number,
    applicationRefName: string,
    actionType: string,
    conversationFlowName: string,
    conversationBuilderConfigId: string,
    apiKey: string
  ) {
    const payload = {
      company_id: companyId,
      application_ref_name: applicationRefName,
      action_type: actionType,
      conversation_flow_name: conversationFlowName,
      conversation_builder_config_id: conversationBuilderConfigId,
    };

    return GeneralService.post(
      "/conversation_builder/create_conversation_tree_resources",
      JSON.stringify(payload),
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getConversationTreeExecutionStatus(
    conversationBuilderConfigId: string,
    apiKey: string
  ) {
    return GeneralService.get(
      `/conversation_builder/execution_status/${conversationBuilderConfigId}`,
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static validateConversationFlowName(
    companyId: number,
    applicationId: number,
    intentId: number,
    conversationFlowName: string,
    apiKey: string,
    agentId?: number,
    excludeConfigId?: number
  ) {
    const payload = {
      company_id: companyId,
      application_id: applicationId,
      intent_id: intentId,
      conversation_flow_name: conversationFlowName,
      agent_id: agentId ?? null,
      exclude_config_id: excludeConfigId ?? null,
    };

    return GeneralService.post(
      "/conversation_builder/validate_conversation_flow_name",
      JSON.stringify(payload),
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteConversationTree(
    conversationTreeId: number,
    apiKey: string
  ) {
    return GeneralService.delete(
      `/conversation_builder/${conversationTreeId}`,
      "",
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteConversationTreeVersion(
    conversationTreeId: number,
    conversationTreeVersionId: number,
    apiKey: string
  ) {
    return GeneralService.delete(
      `/conversation_builder/${conversationTreeId}/${conversationTreeVersionId}`,
      "",
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }
}
